import React, { useState, useRef } from "react";
import {
  MessageSquare, X, Loader2, Sparkles, Upload, Check, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappPreview, createGroup, errorMessage } from "../../lib/api";

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/** WhatsApp Import — owner pastes a chat or drops a screenshot, MyAE extracts
 * members + sections + deadline, owner reviews and confirms, group is created. */
export default function WhatsAppImportModal({ onClose, onCreated }) {
  const [step, setStep] = useState("input");  // input | preview
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const extract = async () => {
    if (!text.trim() && !imageFile) {
      return toast.error("Paste the chat text or drop a screenshot first.");
    }
    setBusy(true);
    try {
      const payload = {};
      if (text.trim()) payload.text = text.trim();
      if (imageFile) payload.image_base64 = await readFileAsBase64(imageFile);
      const res = await whatsappPreview(payload);
      setPreview(res);
      setStep("preview");
    } catch (e) {
      toast.error(errorMessage(e, "Could not extract the group."));
    } finally { setBusy(false); }
  };

  const editSection = (idx, key, val) => {
    setPreview((p) => {
      const sections = [...p.sections];
      sections[idx] = { ...sections[idx], [key]: val };
      return { ...p, sections };
    });
  };
  const removeSection = (idx) => {
    setPreview((p) => {
      const sections = [...p.sections];
      sections.splice(idx, 1);
      return { ...p, sections };
    });
  };
  const removeMember = (idx) => {
    setPreview((p) => {
      const members = [...p.members];
      members.splice(idx, 1);
      return { ...p, members };
    });
  };

  const confirm = async () => {
    if (!preview) return;
    if (preview.sections.length === 0) {
      return toast.error("Add at least one section before creating the group.");
    }
    setBusy(true);
    try {
      const grp = await createGroup({
        title: (preview.title || "Untitled Group").trim(),
        module: "",
        max_members: Math.max(preview.members.length + 1, 4),
        deadline: preview.deadline || "",
        sections: preview.sections.map((s, i) => ({
          title: s.title,
          order: i,
          purpose: s.purpose || "",
        })),
      });
      toast.success("Group created — invite link is ready.");
      onCreated?.(grp);
    } catch (e) {
      toast.error(errorMessage(e, "Could not create the group."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-auto p-4" data-testid="whatsapp-import-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mt-8 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 text-white px-6 py-4 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare size={16} />
              <span className="text-[10px] uppercase tracking-widest font-semibold">Import from WhatsApp</span>
            </div>
            <h3 className="font-display text-xl font-semibold">Build the group from a chat</h3>
            <p className="text-sm text-emerald-100 mt-1">
              Paste the group chat or drop a screenshot. MyAE extracts members, sections and the deadline for you.
            </p>
          </div>
          <button onClick={onClose} data-testid="whatsapp-close" className="text-emerald-200 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-6">
          {step === "input" && (
            <>
              <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-1">Chat text</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder={"Paste the group chat here. Include names + section allocations.\n\ne.g.\nAlex: I'll take the literature review\nJenna: I'll do the introduction and background\n..."}
                data-testid="whatsapp-text-input"
                className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
              />
              <div className="mt-3 text-xs text-zinc-500 flex items-center gap-2">
                <span className="text-zinc-400">or</span>
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="whatsapp-image-btn"
                  className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-semibold border border-emerald-200 rounded-md px-2 py-1"
                >
                  <Upload size={12} /> Upload a screenshot
                </button>
                {imageFile && (
                  <span className="text-zinc-700 inline-flex items-center gap-1">
                    <Check size={12} className="text-emerald-600" /> {imageFile.name}
                    <button onClick={() => setImageFile(null)} className="text-zinc-400 hover:text-zinc-700 ml-1"><Trash2 size={11} /></button>
                  </span>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="text-sm text-zinc-600 hover:text-zinc-900 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={extract}
                  disabled={busy || (!text.trim() && !imageFile)}
                  data-testid="whatsapp-extract-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Extract with AI
                </button>
              </div>
            </>
          )}

          {step === "preview" && preview && (
            <>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-1">Group title</label>
                <input
                  type="text"
                  value={preview.title}
                  onChange={(e) => setPreview({ ...preview, title: e.target.value })}
                  data-testid="whatsapp-preview-title"
                  className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-1">Deadline</label>
                  <input
                    type="date"
                    value={preview.deadline || ""}
                    onChange={(e) => setPreview({ ...preview, deadline: e.target.value })}
                    data-testid="whatsapp-preview-deadline"
                    className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-1">AI Confidence</label>
                  <div className={`inline-block text-xs font-semibold uppercase tracking-wide rounded-full border px-3 py-1.5 ${
                    preview.confidence === "high" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
                    preview.confidence === "low" ? "bg-red-50 border-red-200 text-red-700" :
                    "bg-amber-50 border-amber-200 text-amber-700"
                  }`}>
                    {preview.confidence}
                  </div>
                </div>
              </div>
              {preview.notes && (
                <p className="text-xs text-zinc-500 italic mb-4">{preview.notes}</p>
              )}

              {/* Members */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2">Members ({preview.members.length})</p>
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-2 space-y-1" data-testid="whatsapp-preview-members">
                  {preview.members.length === 0 && <p className="text-xs text-zinc-500 italic px-2 py-1">No members extracted.</p>}
                  {preview.members.map((m, i) => (
                    <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-white rounded" data-testid={`whatsapp-member-${i}`}>
                      <span className="text-sm text-zinc-900">{m.name}</span>
                      <div className="flex items-center gap-2">
                        {m.role_hint && <span className="text-[10px] text-zinc-500">{m.role_hint}</span>}
                        <button onClick={() => removeMember(i)} className="text-zinc-400 hover:text-red-600 p-0.5" data-testid={`whatsapp-member-remove-${i}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">Members will need to accept the invite link after the group is created.</p>
              </div>

              {/* Sections */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wide mb-2">Sections ({preview.sections.length})</p>
                <div className="space-y-2" data-testid="whatsapp-preview-sections">
                  {preview.sections.map((s, i) => (
                    <div key={i} className="rounded-lg border border-zinc-200 bg-white p-2 flex items-center gap-2" data-testid={`whatsapp-section-${i}`}>
                      <span className="text-xs text-zinc-400 font-mono tabular-nums w-5">{i + 1}.</span>
                      <input
                        value={s.title}
                        onChange={(e) => editSection(i, "title", e.target.value)}
                        data-testid={`whatsapp-section-title-${i}`}
                        className="flex-1 text-sm rounded border border-zinc-200 px-2 py-1"
                        placeholder="Section title"
                      />
                      <input
                        value={s.assigned_to || ""}
                        onChange={(e) => editSection(i, "assigned_to", e.target.value)}
                        data-testid={`whatsapp-section-assignee-${i}`}
                        placeholder="Owner"
                        className="w-32 text-sm rounded border border-zinc-200 px-2 py-1"
                      />
                      <button onClick={() => removeSection(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`whatsapp-section-remove-${i}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between gap-2">
                <button
                  onClick={() => setStep("input")}
                  className="text-sm text-zinc-600 hover:text-zinc-900 px-4 py-2"
                >
                  Back
                </button>
                <button
                  onClick={confirm}
                  disabled={busy || preview.sections.length === 0}
                  data-testid="whatsapp-confirm-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-4 py-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Create group
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
